"use client";

import { useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { Article } from "@/data/articles";
import { ArticleCard } from "./ArticleCard";
import { ArrowLeft, Calendar, Tag } from "lucide-react";

interface Props {
  article: Article;
  related: Article[];
  cat: { badge: string; ring: string };
  formattedDate: string;
}

export function ArticleDetailClient({
  article,
  related,
  cat,
  formattedDate,
}: Props) {
  // Scroll to top when slug changes (client-side navigation)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [article.slug]);

  return (
    <div className="min-h-screen bg-white mt-10">
      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <header className="relative h-[60vh] min-h-[420px] w-full overflow-hidden">
        <Image
          src={`${article.thumbnail}`}
          alt={article.title}
          fill
          priority
          sizes="100vw"
          className="object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/55 to-black/75" />
        <div className="relative h-full flex flex-col items-center justify-center text-center px-4">
          <span className="inline-flex items-center rounded-full border px-4 py-1 text-xs font-medium mb-6 backdrop-blur bg-white/15 text-white border-white/30">
            {article.category}
          </span>
          <h1 className="font-serif-display text-white text-3xl md:text-5xl lg:text-6xl max-w-4xl leading-tight">
            {article.title}
          </h1>
          <p className="mt-6 text-white/80 text-sm md:text-base">
            {formattedDate}
          </p>
        </div>
      </header>

      {/* ── Meta row ──────────────────────────────────────────────────── */}
      <div className="border-b border-healing-100 bg-healing-50/30">
        <div className="container mx-auto px-4 py-4 flex flex-wrap items-center justify-between gap-4 text-sm">
          <Link
            href="/articles"
            className="inline-flex items-center gap-2 text-healing-700 hover:text-healing-900 font-medium transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Articles
          </Link>
          <div className="flex items-center gap-5 text-muted-foreground">
            <span className="inline-flex items-center gap-2">
              <Calendar className="h-4 w-4 text-healing-600" />
              {formattedDate}
            </span>
            <span
              className={[
                "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium",
                cat.badge,
              ].join(" ")}
            >
              <Tag className="h-3 w-3" />
              {article.category}
            </span>
          </div>
        </div>
      </div>

      {/* ── Article body ──────────────────────────────────────────────── */}
      <article className="container mx-auto px-4 py-14 lg:py-20">
        <div
          className={[
            "mx-auto max-w-none",
            "prose prose-lg",
            // Headings
            "prose-headings:font-serif-display prose-headings:text-healing-900",
            "prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4",
            "prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3",
            // Paragraphs
            "prose-p:text-gray-700 prose-p:leading-[1.85] prose-p:mb-5",
            // Lead paragraph
            "[&_.lead]:text-xl [&_.lead]:text-gray-600 [&_.lead]:font-medium [&_.lead]:leading-relaxed [&_.lead]:mb-8",
            // Strong & em
            "prose-strong:text-healing-800 prose-strong:font-semibold",
            "prose-em:text-gray-600 prose-em:italic",
            // Lists
            "prose-ul:my-6 prose-ul:space-y-2 prose-li:text-gray-700",
            "prose-ol:my-6 prose-ol:space-y-2",
            // Links
            "prose-a:text-healing-700 prose-a:underline hover:prose-a:text-healing-900",
            // Blockquote
            "prose-blockquote:border-l-4 prose-blockquote:border-healing-400",
            "prose-blockquote:bg-healing-50/50 prose-blockquote:px-6 prose-blockquote:py-4",
            "prose-blockquote:rounded-r-lg prose-blockquote:not-italic",
            "prose-blockquote:text-healing-800",
          ].join(" ")}
          dangerouslySetInnerHTML={{ __html: article.content }}
        />
      </article>

      {/* ── Related articles ──────────────────────────────────────────── */}
      {related.length > 0 && (
        <section className="bg-healing-50/40 border-t border-healing-100 py-16 lg:py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <p className="text-xs uppercase tracking-[0.25em] text-healing-600 font-semibold mb-3">
                Continue Reading
              </p>
              <h2 className="font-serif-display text-3xl md:text-4xl text-healing-900">
                Related Articles
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {related.map((a) => (
                <ArticleCard key={a.slug} article={a} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
